import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  ApiService,
  TrustMatchCard,
} from '../../shared/services/api.service';
import { ConsentFlowService } from '../../shared/services/consent-flow.service';

@Component({
  selector: 'app-trust-data-viewer',
  templateUrl: './trust-data-viewer.component.html',
  styleUrls: ['./trust-data-viewer.component.css'],
})
export class TrustDataViewerComponent implements OnInit, OnDestroy {
  requestId = '';
  trustNumber: string | null = null;
  lookupDisplay: string | null = null;
  backLabel = 'Back to search history';
  matches: TrustMatchCard[] = [];
  loading = false;
  waitingForRequest = false;
  requestStatus: 'IN_PROGRESS' | 'FAILED' | 'COMPLETE' | 'CANCELLED' | 'QUEUED' | null = null;
  requestResultCode: 'NO_MATCH' | null = null;
  requestErrorReason: string | null = null;
  requestErrorReasonCode: string | null = null;
  loadDataErrorMessage: string | null = null;
  freeUntil: string | null = null;
  dataRetrievedAt: string | null = null;
  trusteeColumns: string[] = ['fullName', 'surname', 'idNumber', 'age', 'status', 'riskBand'];

  private backTarget: 'history' | 'trust-request' | 'user-trust-request' | 'multi-search' | 'user-multi-search' =
    'history';
  private backBatchId: string | null = null;
  private statusPollingSub: Subscription | null = null;
  private hasLoadedData = false;
  private resolvingConsentPrompt = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private consentFlowService: ConsentFlowService
  ) {}

  ngOnInit(): void {
    this.requestId = this.route.snapshot.paramMap.get('requestId') || '';
    this.loading = true;
    const from = this.route.snapshot.queryParamMap.get('from');
    const fromBatchId = this.route.snapshot.queryParamMap.get('batchId');
    if (from === 'trust-request') {
      this.backTarget = 'trust-request';
      this.backLabel = 'Back to trust search';
    } else if (from === 'user-trust-request') {
      this.backTarget = 'user-trust-request';
      this.backLabel = 'Back to trust search';
    } else if (from === 'multi-search' && fromBatchId) {
      this.backTarget = 'multi-search';
      this.backBatchId = fromBatchId;
      this.backLabel = 'Back to multi search';
    } else if (from === 'user-multi-search' && fromBatchId) {
      this.backTarget = 'user-multi-search';
      this.backBatchId = fromBatchId;
      this.backLabel = 'Back to multi search';
    } else {
      this.backTarget = 'history';
      this.backLabel = 'Back to search history';
    }
    this.refreshRequestStateAndData();
  }

  ngOnDestroy(): void {
    this.stopStatusPolling();
  }

  goBack(): void {
    if (this.backTarget === 'trust-request') {
      void this.router.navigate(['/admin/trust-request']);
      return;
    }
    if (this.backTarget === 'user-trust-request') {
      void this.router.navigate(['/user/trust-request']);
      return;
    }
    if (this.backTarget === 'multi-search' && this.backBatchId) {
      void this.router.navigate(['/admin/multi-search', this.backBatchId]);
      return;
    }
    if (this.backTarget === 'user-multi-search' && this.backBatchId) {
      void this.router.navigate(['/user/multi-search', this.backBatchId]);
      return;
    }
    void this.router.navigate(['/admin/history']);
  }

  newTrustSearch(): void {
    const target = this.router.url.startsWith('/user/') ? '/user/trust-request' : '/admin/trust-request';
    void this.router.navigate([target]);
  }

  formatRequestStatusLabel(): string {
    if (!this.requestStatus) return 'Queued';
    return this.requestStatus.replace(/_/g, ' ');
  }

  getRequestFailureMessage(): string {
    const fallback = 'No result is available for this request.';
    if (!this.requestErrorReason) return fallback;
    if (!this.requestErrorReasonCode) return this.requestErrorReason;
    return `${this.requestErrorReason} (Code: ${this.requestErrorReasonCode})`;
  }

  getSearchedTrustLabel(): string {
    return this.trustNumber || this.lookupDisplay || '—';
  }

  formatFreeUntil(): string | null {
    if (!this.freeUntil) return null;
    const date = new Date(this.freeUntil);
    if (isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  }

  private refreshRequestStateAndData(): void {
    this.apiService.getRequestStatus(this.requestId).subscribe({
      next: (response) => {
        this.trustNumber = response.trustNumber || null;
        this.lookupDisplay = response.lookupDisplay || null;
        this.requestStatus = response.status;
        this.requestResultCode = response.resultCode || null;
        this.requestErrorReason = response.errorReason || null;
        this.requestErrorReasonCode = response.errorReasonCode || null;

        if (response.status === 'COMPLETE') {
          this.waitingForRequest = false;
          this.stopStatusPolling();
          if (!this.hasLoadedData) {
            this.loadData();
          } else {
            this.loading = false;
          }
          return;
        }

        if (response.status === 'QUEUED' || response.status === 'IN_PROGRESS') {
          this.loading = false;
          this.waitingForRequest = true;
          this.startStatusPolling();
          return;
        }

        this.loading = false;
        this.waitingForRequest = false;
        this.stopStatusPolling();
      },
      error: (error) => {
        this.loading = false;
        const errorMessage = error?.error?.error?.message || 'Failed to load request status';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
      },
    });
  }

  private startStatusPolling(): void {
    if (this.statusPollingSub) return;
    this.statusPollingSub = interval(4000)
      .pipe(switchMap(() => this.apiService.getRequestStatus(this.requestId)))
      .subscribe({
        next: (response) => {
          this.trustNumber = response.trustNumber || null;
          this.lookupDisplay = response.lookupDisplay || null;
          this.requestStatus = response.status;
          this.requestResultCode = response.resultCode || null;
          this.requestErrorReason = response.errorReason || null;
          this.requestErrorReasonCode = response.errorReasonCode || null;

          if (response.status === 'COMPLETE') {
            this.waitingForRequest = false;
            this.stopStatusPolling();
            if (!this.hasLoadedData) {
              this.loadData();
            }
            return;
          }

          if (response.status === 'QUEUED' || response.status === 'IN_PROGRESS') {
            this.waitingForRequest = true;
            return;
          }

          this.waitingForRequest = false;
          this.stopStatusPolling();
        },
        error: () => {
          // Best-effort polling; keep waiting state.
        },
      });
  }

  private stopStatusPolling(): void {
    this.statusPollingSub?.unsubscribe();
    this.statusPollingSub = null;
  }

  private loadData(): void {
    this.loading = true;
    this.apiService.getRequestData(this.requestId).subscribe({
      next: (response) => {
        this.loading = false;
        this.hasLoadedData = true;
        this.matches = Array.isArray(response.matches) ? response.matches : [];
        this.dataRetrievedAt = response.dataRetrievedAt || null;
        this.freeUntil = response.cooldownFreeUntil || null;
        if (response.resultCode === 'NO_MATCH' || this.matches.length === 0) {
          this.requestResultCode = 'NO_MATCH';
          this.loadDataErrorMessage = 'No trust matches were found for this trust number.';
        } else {
          this.loadDataErrorMessage = null;
        }
      },
      error: (error) => {
        this.loading = false;
        this.hasLoadedData = false;
        this.matches = [];
        this.freeUntil = null;
        this.loadDataErrorMessage = null;
        if (this.consentFlowService.isConsentRequiredError(error)) {
          void this.promptForConsentAndRetryDataLoad();
          return;
        }
        const errorMessage = error?.error?.error?.message || 'Failed to load trust data';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
      },
    });
  }

  private async promptForConsentAndRetryDataLoad(): Promise<void> {
    if (this.resolvingConsentPrompt) return;
    this.resolvingConsentPrompt = true;

    try {
      this.loading = false;
      const consentAccepted = await this.consentFlowService.ensureConsentAccepted();
      if (!consentAccepted) {
        return;
      }
      this.loadData();
    } catch (error) {
      this.loading = false;
      this.snackBar.open('An error occurred while processing POPIA acknowledgement', 'Close', { duration: 5000 });
    } finally {
      this.resolvingConsentPrompt = false;
    }
  }
}
